/**
 * Web Push, from the browser's side.
 *
 * The hard part is not the API, it is telling the truth about where it works:
 *
 *  - Chrome, Edge, Firefox — everywhere, straight away.
 *  - Safari on macOS 16+ — straight away.
 *  - Safari on iOS 16.4+ — ONLY once the site is on the Home Screen. Apple
 *    allows no notifications from a Safari tab at all. `Notification` is not
 *    even defined there, so the toggle would simply not work and the user would
 *    be left to guess why.
 *
 * That last case is the one that matters here, so it is detected explicitly and
 * explained rather than failing quietly.
 */

export type PushState =
  | "unsupported" // the browser has no Push API at all
  | "needs_install" // iOS Safari in a tab: works, but only from the Home Screen
  | "not_configured" // the server has no VAPID keys
  | "denied" // the user said no, and only they can undo it
  | "off" // available, permission not yet asked or granted
  | "on";

/** Standalone means launched from the Home Screen rather than a Safari tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari's own, non-standard flag. Still the only reliable signal on iOS.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac; the touch points give it away.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function hasApi(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Base64url → Uint8Array, which is the only shape applicationServerKey takes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let registration: ServiceWorkerRegistration | null = null;

/**
 * Registers the worker. Safe to call repeatedly — the browser returns the same
 * registration — and it is called on boot rather than on the settings screen,
 * because the worker also has to be alive to receive a push at all.
 */
export async function ensureWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return registration;
  } catch {
    return null;
  }
}

export interface PushStatus {
  state: PushState;
  /** The endpoint this device is currently registered under, if any. */
  endpoint: string | null;
}

export async function status(serverEnabled: boolean): Promise<PushStatus> {
  if (!hasApi()) {
    // iOS is the one case where "unsupported" is wrong and misleading: it works
    // perfectly, once installed.
    return { state: isIos() && !isStandalone() ? "needs_install" : "unsupported", endpoint: null };
  }
  if (!serverEnabled) return { state: "not_configured", endpoint: null };
  if (Notification.permission === "denied") return { state: "denied", endpoint: null };

  const reg = await ensureWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub
    ? { state: "on", endpoint: sub.endpoint }
    : { state: "off", endpoint: null };
}

export type EnableResult =
  | { ok: true; endpoint: string }
  | { ok: false; reason: "denied" | "dismissed" | "failed"; detail?: string };

/**
 * Ask for permission and register.
 *
 * Called only from an explicit tap on the settings toggle, never on load. A
 * permission prompt fired at a stranger is refused most of the time, and a
 * refusal is close to permanent — the browser will not ask again and the user
 * has to dig through site settings to undo it. One prompt, at the moment
 * somebody asked for it.
 */
export async function enable(publicKey: string): Promise<EnableResult> {
  try {
    const permission = await Notification.requestPermission();
    if (permission === "denied") return { ok: false, reason: "denied" };
    if (permission !== "granted") return { ok: false, reason: "dismissed" };

    const reg = await ensureWorker();
    if (!reg) return { ok: false, reason: "failed", detail: "no_service_worker" };

    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        // Required by every browser: a push that does not show a notification
        // is not allowed, and Chrome revokes the permission over it.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      }));

    return { ok: true, endpoint: sub.endpoint };
  } catch (e) {
    return { ok: false, reason: "failed", detail: (e as Error).message };
  }
}

/** Unregister this device. Returns the endpoint that was removed, if any. */
export async function disable(): Promise<string | null> {
  const reg = await ensureWorker();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}

/** The JSON shape the server wants. */
export async function currentSubscription(): Promise<PushSubscriptionJSON | null> {
  const reg = await ensureWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? sub.toJSON() : null;
}
